"use client"

import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { UserNotificationFeedItem } from "@/gql/graphql"
import CreativeWorkProjectLike from "./CreativeWorkProjectLike"
import { CreativeWorkProjectContributorTag } from "./CreativeWorkProjectContributorTag"
import { ReceivedConnectionRequest } from "./ReceivedConnectionRequest"
import { SentConnectionRequestAccepted } from "./SentConnectionRequestAccepted"
import { JobApplicationReceived } from "./JobApplicationReceived"
import { JobInvitationReceived } from "./JobInvitationReceived"
import { SubmittedJobInvitationAccepted } from "./SubmittedJobInvitationAccepted"
import { ReceivedReferralRequest } from "./ReceivedReferralRequest"
import { UnknownActivity } from "./UnknownActivity"
import { JobRecommendationBatchCreated } from "./JobRecommendationBatchCreated"
import { JobPostingClosed } from "./JobPostingClosed"
import { ContactJoined } from "./ContactJoined"
import { ApplicationShortlisted } from "./ApplicationShortlisted"
import { JobRecommendation } from "./JobRecommendation"
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver"
import { useRouter } from "next-nprogress-bar"
import { useSidebar } from "../Sidebar/useSidebar"
import { ampli } from "@/ampli"
import type { NovaSession } from "@/types"
import { SeenNotification } from "./Notifications"

const NotificationItemFragment = graphql(`
  fragment NotificationItem on StreamNotificationFeedAggregatedActivity {
    id
    __typename
    isSeen
    activityCount
    group
    activities {
      id
      notificationType
      actor {
        id
        ... on IndividualCreative {
          handle
        }
        ... on IndividualClient {
          handle
        }
      }
      foreignObject {
        __typename
        ... on ConnectionRequest {
          id
        }
      }
    }
    ...CreativeWorkProjectLike
    ...CreativeWorkProjectContributorTag
    ...ReceivedConnectionRequest
    ...SentConnectionRequestAccepted
    ...JobApplicationReceived
    ...JobInvitationReceived
    ...SubmittedJobInvitationAccepted
    ...ReceivedReferralRequest
    ...UnknownActivity
    ...JobRecommendationBatchCreated
    ...JobPostingClosed
    ...ContactJoined
    ...ApplicationShortlisted
    ...JobRecommendation
  }
`)

interface Props {
  item: FragmentType<typeof NotificationItemFragment>
  onView: (notif: SeenNotification) => void
  serverSession: NovaSession
}

export const NotificationItem: React.FC<Props> = ({ item: itemFragment, onView, serverSession }) => {
  const router = useRouter()
  const { closeSecondaryBar } = useSidebar()

  const item = useFragment(NotificationItemFragment, itemFragment)
  const itemRef = useRef<HTMLDivElement | null>(null)

  const activity = item?.activities?.[0]
  const notificationType = activity?.notificationType

  const handleIntersection = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        onView({
          id: item.id!,
          group: item.group!,
          isSeen: item.isSeen!,
        })
      }
    },
    [item, onView]
  )

  const onClick = (type: UserNotificationFeedItem) => {
    ampli.notificationOpen({
      notification_type: type || "",
    })
  }

  const { observe, unobserve } = useIntersectionObserver(handleIntersection)

  useEffect(() => {
    const currentElement = itemRef.current
    if (currentElement) observe(currentElement)

    return () => {
      if (currentElement) unobserve(currentElement)
    }
  }, [observe, unobserve])

  const onActorClick = useCallback(
    (e: React.MouseEvent<any, MouseEvent>) => {
      e.preventDefault()
      const creativeHandle = activity?.actor?.__typename === "IndividualCreative" && activity?.actor?.handle
      const clientHandle = activity?.actor?.__typename === "IndividualClient" && activity?.actor?.handle

      if (creativeHandle) {
        router.push(`/${creativeHandle}`)
      } else if (clientHandle) {
        router.push(`/clients/${clientHandle}`)
      }
      closeSecondaryBar()
    },
    [activity, closeSecondaryBar, router]
  )

  const renderNotificationComponent = useMemo(() => {
    switch (notificationType) {
      case UserNotificationFeedItem.CreativeWorkProjectLiked:
        return <CreativeWorkProjectLike onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.AddedAsCreativeWorkProjectContributor:
        return <CreativeWorkProjectContributorTag onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.ConnectionRequestReceived:
        return <ReceivedConnectionRequest onActorClick={onActorClick} item={item} serverSession={serverSession} />
      case UserNotificationFeedItem.ConnectionRequestAccepted:
        return <SentConnectionRequestAccepted onActorClick={onActorClick} item={item} />
      case UserNotificationFeedItem.JobApplicationReceived:
        return <JobApplicationReceived onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.JobInvitationReceived:
        return <JobInvitationReceived onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.JobInvitationAccepted:
        return <SubmittedJobInvitationAccepted onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.CreativeReferralRequested:
        return <ReceivedReferralRequest onActorClick={onActorClick} item={item} />
      case UserNotificationFeedItem.JobRecommendations:
        return <JobRecommendationBatchCreated onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.JobApplicationJobPostingClosed:
        return <JobPostingClosed onActorClick={onActorClick} item={item} />
      case UserNotificationFeedItem.ContactJoined:
        return <ContactJoined onActorClick={onActorClick} item={item} />
      case UserNotificationFeedItem.JobApplicationShortlisted:
        return <ApplicationShortlisted onActorClick={onActorClick} item={item} onClick={onClick} />
      case UserNotificationFeedItem.JobRecommendation:
        return <JobRecommendation onActorClick={onActorClick} item={item} onClick={onClick} />
      default:
        return <UnknownActivity onActorClick={onActorClick} item={item} />
    }
  }, [notificationType, item, onActorClick, serverSession])

  if (!notificationType) {
    return null
  }

  return (
    <div
      ref={itemRef}
      onClick={() => {
        // ampli.notificationOpen({
        //   account_type: serverSession.account || "",
        //   notifcation_type: notificationType,
        // })
      }}>
      {renderNotificationComponent}
    </div>
  )
}
